import React, { useEffect, useState } from "react";
import { c, jt, t } from "ttag";

import { reverse } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { loadCategory } from "../../../api/products";
import { ConfiguratorProductPrice } from "../../../common/ConfiguratorProductPrice";
import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { Link } from "../../../common/Link";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { IProduct } from "../../../models/catalogue";
import {
    IProductCategoryID,
    isoProductCategoryID,
    isoWebPageURL,
} from "../../../models/nominals";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { format } from "../../../utils/format";
import { notEmpty } from "../../../utils/functional";
import { useCurrentBreakpoint } from "../../../utils/hooks";
import { roundReviewRating } from "../../../utils/math";
import { sortProductOptions } from "../../../utils/sorting";
import { DiscountDisplayMode } from "../../configurator/constants";
import { ATTRIBUTE_CLASSES, ShopTileType } from "../ShopCompareTypes";
import { AttributeList } from "../elements/AttributeList";

import styles from "./index.module.scss";

interface IShopTileContentProps {
    value: ShopTileType;
    tilesetId: number;
    compactPricing: boolean;
    selectedSize: string;
}

export const ShopTile = (props: IShopTileContentProps) => {
    const shopTile = props.value.shop_tile;
    const category = shopTile.category?.id;
    const currentBreakpoint = useCurrentBreakpoint();
    const shouldUseCompact =
        props.compactPricing && !currentBreakpoint.belowLaptop;
    const [categoryNumReviews, setCategoryNumReviews] = useState(0);
    const [categoryRating, setCategoryRating] = useState(0);

    const [rootProducts, setRootProducts] = useState<ReadonlyArray<IProduct>>(
        [],
    );

    const loadProducts = async (
        productCategoryID: IProductCategoryID,
    ): Promise<ReadonlyArray<IProduct>> => {
        return await loadCategory(productCategoryID);
    };

    useEffect(() => {
        if (category) {
            loadProducts(isoProductCategoryID.wrap(category))
                .then((products) => {
                    setRootProducts(products);
                })
                .catch((err) => {
                    console.warn(
                        `ShopTile ${shopTile.title} Failed to load products for Category ${category}:`,
                        err,
                    );
                });
        }
    }, [category]);

    useEffect(() => {
        // Collate review number and rating from all products in the category
        const numReviews = rootProducts.reduce((total, product) => {
            return total + product.num_reviews;
        }, 0);
        const ratingWeight = rootProducts.reduce((total, product) => {
            return total + (product.rating ?? 0) * product.num_reviews;
        }, 0);
        const rating = numReviews > 0 ? ratingWeight / numReviews : 0;

        setCategoryNumReviews(numReviews);
        setCategoryRating(roundReviewRating(rating));
    }, [rootProducts]);

    // Collate option_feel values from all products in rootProducts (i.e. all products from this Shop Tile's category)
    const categoryFeelOptions = rootProducts
        .map((product) => {
            // Check if product has variants
            if (product.children && product.children.length > 0) {
                return product.children
                    .map((child) => child.attributes.option_feel?.value)
                    .filter(notEmpty);
            } else {
                // Fallback to parent product's feel options
                return product.attributes.option_feel?.value;
            }
        })
        .filter(notEmpty)
        .flat();

    // Flatten options again to remove multi-dimensional array
    const flattenedFeelOptions = categoryFeelOptions.flat().filter(notEmpty);
    const allFeelOptions = [...new Set(flattenedFeelOptions)];

    const sortedFeelOptions = sortProductOptions("option_feel", allFeelOptions);
    const attributeList = sortedFeelOptions.map((feelOption) => ({
        title: feelOption,
        class:
            (
                Object.keys(ATTRIBUTE_CLASSES) as Array<
                    keyof typeof ATTRIBUTE_CLASSES
                >
            ).find((key) => ATTRIBUTE_CLASSES[key] === feelOption) || "",
    }));

    const sizePriceMap = shopTile?.size_price_map;
    const effectivePrice =
        sizePriceMap && sizePriceMap[props.selectedSize]
            ? sizePriceMap[props.selectedSize]
            : shopTile.price;
    const totalPrice = getProductPrice(effectivePrice, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    // Following variables are necessary for i18n of the financing string
    const formattedTotalPrice = format.money(totalPrice);
    const financingMonthsLength =
        effectivePrice.per_month_term_length?.toString();
    const financingMonths = effectivePrice.per_month_term_length ? (
        <span key="financing-months">
            {t`${financingMonthsLength} months`}
            <sup>1</sup>
        </span>
    ) : null;
    const financingMonthsSuperscript = (
        <sup key="financing-months-supscript-2">2</sup>
    );

    return (
        <div className={styles.tileContainer}>
            {shopTile && shopTile.primary_flair && (
                <div
                    className={`${
                        shouldUseCompact
                            ? styles.primaryFlairCompact
                            : styles.primaryFlair
                    }`}
                >
                    <Flair flair={shopTile.primary_flair} />
                </div>
            )}
            {shopTile && (
                <div className={styles.headerContainer}>
                    {shopTile.product_image && (
                        <Image
                            alt={shopTile.title}
                            src={shopTile.product_image.url}
                        />
                    )}
                    <div
                        className={`${
                            shouldUseCompact
                                ? styles.contentContainerCompact
                                : styles.contentContainer
                        }`}
                    >
                        {shopTile.secondary_flair && (
                            <div
                                className={`${
                                    shouldUseCompact
                                        ? styles.secondaryFlairCompact
                                        : styles.secondaryFlair
                                }`}
                            >
                                <Flair flair={shopTile.secondary_flair} />
                            </div>
                        )}
                        <RichText
                            html={shopTile?.title || ""}
                            className={`${
                                shouldUseCompact
                                    ? styles.titleCompact
                                    : styles.title
                            }`}
                        />
                        <Link
                            href={isoWebPageURL.wrap(
                                `${shopTile.link}#reviews_body`,
                            )}
                            className={styles.starRating}
                        >
                            <RatingGraphic
                                cardClass={styles.starRating}
                                cardSize="large"
                                numReviews={
                                    categoryNumReviews > 0
                                        ? categoryNumReviews
                                        : shopTile.num_reviews
                                }
                                rating={
                                    categoryNumReviews > 0
                                        ? roundReviewRating(categoryRating)
                                        : (shopTile.rating ?? null)
                                }
                                reviewNumberClass={styles.reviewLink}
                            />
                        </Link>
                        <RichText
                            html={shopTile?.description || ""}
                            className={`${
                                shouldUseCompact
                                    ? styles.descriptionCompact
                                    : styles.description
                            }`}
                        />
                        <div className={styles.attributeList}>
                            <AttributeList list={attributeList} />
                        </div>
                    </div>
                </div>
            )}
            <div
                className={`${
                    shouldUseCompact
                        ? styles.priceContainer
                        : styles.priceContainerHorizontal
                }`}
            >
                {effectivePrice && totalPrice && (
                    <div>
                        <ConfiguratorProductPrice
                            price={effectivePrice}
                            strikeThroughMSRP={false}
                            discountDisplayMode={
                                DiscountDisplayMode.SHOW_ABSOLUTE
                            }
                            actualPriceStyle={"shop"}
                        />
                        {effectivePrice.per_month_term_length && (
                            <div
                                className={`${
                                    shouldUseCompact
                                        ? styles.financingPlanContainer
                                        : styles.financingPlanContainerHorizontal
                                }`}
                            >
                                <span>
                                    {jt`or ${effectivePrice.per_month}/mo${financingMonthsSuperscript} for ${financingMonths}`}
                                </span>
                                <br />
                                <span>
                                    {c("shop-tile-financing-payments")
                                        .t`total payments of ${formattedTotalPrice}`}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                <div
                    className={`${
                        shouldUseCompact
                            ? styles.buttonsCompact
                            : styles.buttons
                    }`}
                >
                    <Link
                        href={shopTile.link}
                        className="button al-comparisongrid-shopnow"
                    >
                        {t`Shop Now`}
                        <span className="ada-screenreader-only">
                            {interpolate(t`for %(productName)s`, {
                                productName: shopTile.title || "",
                            })}
                        </span>
                    </Link>
                    {shopTile.compare_tiles &&
                        shopTile.compare_tiles.length > 0 && (
                            <a
                                // @ts-expect-error: reverse() expects Django route params, not always typed for FE
                                href={`${reverse("shop-tile", { shop_tile_pk: shopTile.id })}?tileset=${props.tilesetId}`}
                                className={`button al-comparisongrid-compare ${styles.compareButton}`}
                            >
                                {t`Compare`}
                            </a>
                        )}
                </div>
            </div>
        </div>
    );
};
